import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useOperator } from '../../contexts/OperatorContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import formatOrderId from '../../utils/formatOrderId';

const { FiClock, FiCheckCircle, FiAlertCircle, FiFileText, FiUser, FiPhone, FiMail, FiVideo, FiArrowLeft } = FiIcons;

// Mock OrderProcessForm component
const OrderProcessForm = ({ order, onSubmitApplication }) => {
  const [formData, setFormData] = useState({
    applicantName: '',
    email: '',
    phone: '',
    address: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock PDF generation
    const mockPdfUrl = 'https://example.com/mock-application.pdf';
    onSubmitApplication(mockPdfUrl);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <p className="text-sm text-blue-800">
          This is a mock application form. In the real system, this would be the actual application form for {order.service_type}.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Applicant Name</label>
          <input
            type="text"
            value={formData.applicantName}
            onChange={(e) => setFormData(prev => ({ ...prev, applicantName: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter applicant name"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter email address"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter phone number"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            value={formData.address}
            onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter address"
            rows="3"
          />
        </div>
      </div>
      
      <div className="flex justify-end">
        <Button type="submit" className="flex items-center">
          <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2" />
          Generate & Submit Application
        </Button>
      </div>
    </form>
  );
};

// Mock ScreenShareSession component
const ScreenShareSession = ({ customerName, onEndSession }) => {
  return (
    <div className="text-center p-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Screen Sharing with {customerName}
      </h3>
      <p className="text-gray-600 mb-6">
        Screen sharing session would be active here. This is a mock interface.
      </p>
      <Button onClick={onEndSession} variant="outline">
        End Session
      </Button>
    </div>
  );
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { startProcessingOrder, submitCompletedApplication, makeApplicationFeePayment } = useOperator();
  
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showScreenShare, setShowScreenShare] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleUploadApplicationPdf = async (fileArg) => {
    try {
      const file = fileArg || selectedFile;
      if (!file) {
        toast.error('Please select a PDF file to upload');
        return;
      }
      if (file.type !== 'application/pdf') {
        toast.error('Only PDF files are allowed');
        return;
      }
      setUploading(true);
      const safeName = (file.name || 'application.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${orderId}/${safeName}`;
      const { error: upErr } = await supabase
        .storage
        .from('success_forms')
        .upload(filePath, file, { cacheControl: '3600', upsert: true, contentType: file.type });
      if (upErr) {
        console.error('Upload failed:', upErr);
        toast.error('Upload failed. Please try again.');
        return;
      }
      const { data: pub } = supabase.storage.from('success_forms').getPublicUrl(filePath);
      const publicUrl = pub?.publicUrl;
      if (!publicUrl) {
        toast.error('Could not get public URL for the uploaded file');
        return;
      }
      // Update operator_orders and available_orders with submitted PDF
      try {
        const { error: opErr } = await supabase
          .from('operator_orders_a7b5c9')
          .update({ submitted_pdf_url: publicUrl, submitted_at: new Date().toISOString() })
          .eq('order_id', orderId);
        if (opErr) console.warn('operator_orders update warning:', opErr);
      } catch (e) {
        console.warn('operator_orders update failed:', e);
      }
      try {
        const { error: avErr } = await supabase
          .from('available_orders_a7b5c9')
          .update({ submitted_pdf_url: publicUrl })
          .eq('order_id', orderId);
        if (avErr) console.warn('available_orders update warning:', avErr);
      } catch (e) {
        console.warn('available_orders update failed:', e);
      }
      setOrder(prev => ({ ...prev, submitted_pdf_url: publicUrl }));
      setUploadedPdfUrl(publicUrl);
      toast.success('Application PDF uploaded. Click Complete Process to send for admin verification.');
    } catch (e) {
      console.error('Upload application PDF error:', e);
      toast.error('Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleCompleteProcess = async () => {
    try {
      setProcessing(true);
      
      // Validate that PDF is uploaded
      if (!order?.submitted_pdf_url) {
        toast.error('Please upload the application PDF first');
        setProcessing(false);
        return;
      }
      
      console.log('Starting complete process for order:', orderId);
      
      // Update operator_orders status
      const { error: opErr } = await supabase
        .from('operator_orders_a7b5c9')
        .update({ 
          status: 'pdf_submitted'
        })
        .eq('order_id', orderId);
      
      if (opErr) {
        console.error('Error updating operator_orders:', opErr);
        toast.error(`Failed to update order status: ${opErr.message}`);
        setProcessing(false);
        return;
      }
      
      console.log('operator_orders updated successfully');
      
      // Update available_orders - keep status as 'assigned' but mark with submitted_pdf_url
      // This signals to admin that it's ready for verification
      const { error: availErr } = await supabase
        .from('available_orders_a7b5c9')
        .update({ 
          status: 'assigned',  // Keep as assigned (allowed by constraint)
          submitted_pdf_url: order.submitted_pdf_url  // This marks it as ready for admin review
        })
        .eq('order_id', orderId);
      
      if (availErr) {
        console.error('Error updating available_orders:', availErr);
        toast.error(`Failed to send for verification: ${availErr.message}`);
        setProcessing(false);
        return;
      }
      
      console.log('available_orders updated successfully');
      toast.success('✅ Application sent for admin verification');
      navigate('/operator/orders');
    } catch (e) {
      console.error('Complete process error:', e);
      toast.error(`Failed to complete process: ${e.message || 'Unknown error'}`);
    } finally {
      setProcessing(false);
    }
  };

  // Deterministic meeting URL so both operator and customer can join the same room without DB writes
  const getMeetingUrl = (oid) => `https://meet.jit.si/H2A-Order-${oid}`;

  // Extract a readable filename from a URL
  const getFileNameFromUrl = (url) => {
    if (!url) return 'application.pdf';
    try {
      const u = new URL(url);
      const last = u.pathname.split('/').pop();
      return decodeURIComponent(last || 'application.pdf');
    } catch {
      // Fallback for relative URLs
      const parts = (url || '').split('/');
      return decodeURIComponent(parts[parts.length - 1] || 'application.pdf');
    }
  };

  const downloadFile = async (url, fallbackName) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Network error');
      const blob = await res.blob();
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = fallbackName || 'document';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(objectUrl);
      document.body.removeChild(a);
    } catch (e) {
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('operator_orders_a7b5c9')
          .select('*')
          .eq('order_id', orderId)
          .maybeSingle();

        if (error) throw error;
        if (!data) {
          toast.error('Order not found');
          navigate('/operator/orders');
          return;
        }

        setOrder(data);

        // Fetch dynamic customer contact info from customers_h2a first
        try {
          const { data: cust, error: custErr } = await supabase
            .from('customers_h2a')
            .select('full_name, email, phone')
            .eq('user_id', data.customer_id)
            .maybeSingle();
          if (custErr) throw custErr;

          const hasCust = !!cust;
          const emailFromCust = cust?.email || '';
          const phoneFromCust = cust?.phone || '';
          const nameFromCust = cust?.full_name || '';

          // If customers_h2a missing or lacks contacts, fallback to available_orders
          if (!hasCust || (!emailFromCust && !phoneFromCust)) {
            try {
              const { data: avail, error: availErr } = await supabase
                .from('available_orders_a7b5c9')
                .select('customer_name, customer_email, customer_phone')
                .eq('order_id', data.order_id)
                .maybeSingle();
              if (availErr) throw availErr;
              setCustomer({
                id: data.customer_id,
                name: data.customer_name || avail?.customer_name || nameFromCust || 'Customer',
                phone: avail?.customer_phone || phoneFromCust || '',
                email: avail?.customer_email || emailFromCust || ''
              });
            } catch (availFetchErr) {
              console.warn('Failed to fetch inline contact from available_orders:', availFetchErr);
              setCustomer({
                id: data.customer_id,
                name: data.customer_name || nameFromCust || 'Customer',
                phone: phoneFromCust || '',
                email: emailFromCust || ''
              });
            }
          } else {
            setCustomer({
              id: data.customer_id,
              name: data.customer_name || nameFromCust || 'Customer',
              phone: phoneFromCust,
              email: emailFromCust
            });
          }
        } catch (custFetchErr) {
          console.warn('Failed to fetch customer profile by user_id:', custFetchErr);
          // Fallback: attempt to read any inline contact on available_orders_a7b5c9
          try {
            const { data: avail, error: availErr } = await supabase
              .from('available_orders_a7b5c9')
              .select('customer_name, customer_email, customer_phone')
              .eq('order_id', data.order_id)
              .maybeSingle();
            if (availErr) throw availErr;
            setCustomer({
              id: data.customer_id,
              name: data.customer_name || avail?.customer_name || 'Customer',
              phone: avail?.customer_phone || '',
              email: avail?.customer_email || ''
            });
          } catch (availFetchErr) {
            console.warn('Failed to fetch inline contact from available_orders:', availFetchErr);
            setCustomer({
              id: data.customer_id,
              name: data.customer_name || 'Customer',
              phone: '',
              email: ''
            });
          }
        }

        if (data.documents) {
          setDocuments(
            Object.entries(data.documents).map(([name, doc]) => ({
              name,
              url: (doc && doc.url) || '#'
            }))
          );
        } else {
          setDocuments([]);
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        toast.error('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, navigate]);

  const handleStartProcessing = async () => {
    try {
      setProcessing(true);
      const success = await startProcessingOrder(orderId);
      
      if (success) {
        setOrder(prev => ({
          ...prev,
          status: 'in_progress',
          processing_started_at: new Date().toISOString()
        }));
        const url = getMeetingUrl(orderId);
        toast.success('Meeting link generated. Opening room...');
        window.open(url, '_blank', 'noopener');
      }
    } catch (error) {
      console.error('Error starting order processing:', error);
      toast.error('Failed to start processing');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitApplication = async (pdfUrl) => {
    try {
      const success = await submitCompletedApplication(orderId, pdfUrl);
      if (success) {
        setOrder(prev => ({
          ...prev,
          status: 'pdf_submitted',
          completed_at: new Date().toISOString(),
          submitted_pdf_url: pdfUrl
        }));
        toast.success('Application submitted successfully!');
        navigate('/operator/orders');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find the order you're looking for</p>
          <Button onClick={() => navigate('/operator/orders')}>
            Back to Orders
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center mb-4">
            <Link to="/operator/orders" className="text-primary-600 hover:text-primary-700 flex items-center">
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
              Back to Orders
            </Link>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {order.service_type}
              </h1>
              <p className="text-gray-600 mt-1">
                Order ID: <span className="font-medium font-mono">{formatOrderId(order.order_id || order.id)}</span>
              </p>
            </div>
            <div className="mt-2 md:mt-0">
              <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                order.status === 'assigned' ? 'bg-yellow-100 text-yellow-800' :
                order.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                order.status === 'pdf_submitted' ? 'bg-purple-100 text-purple-800' :
                'bg-green-100 text-green-800'
              }`}>
                {order.status === 'assigned' ? 'Assigned' :
                 order.status === 'in_progress' ? 'In Progress' :
                 order.status === 'pdf_submitted' ? 'Submitted for Review' :
                 'Completed'}
              </span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Order Actions */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Order Actions</h2>
              
              {order.status === 'assigned' && (
                <div>
                  <p className="text-gray-700 mb-6">
                    This order has been assigned to you. Please call the customer before starting the application process.
                  </p>
                  <div className="space-y-4">
                    <Button
                      onClick={() => window.open(`tel:${customer?.phone}`)}
                      variant="outline"
                      className="w-full flex items-center justify-center"
                    >
                      <SafeIcon icon={FiPhone} className="mr-2" />
                      Call Customer
                    </Button>
                    
                    <Button
                      onClick={handleStartProcessing}
                      loading={processing}
                      className="w-full flex items-center justify-center"
                    >
                      <SafeIcon icon={FiClock} className="mr-2" />
                      Start Processing
                    </Button>
                  </div>
                </div>
              )}
              
              {order.status === 'in_progress' && !showScreenShare && (
                <div>
                  <div className="mb-6">
                    <h3 className="font-medium text-gray-900 mb-2">Screen Sharing</h3>
                    <p className="text-gray-700 mb-4">
                      Share your screen with the customer while filling the application.
                    </p>
                    <div className="space-y-3">
                      <Button
                        onClick={() => window.open(getMeetingUrl(orderId), '_blank')}
                        className="w-full bg-green-600 hover:bg-green-700  flex items-center justify-center"
                      >
                        <SafeIcon icon={FiVideo} className="mr-2" />
                        Join Meeting
                      </Button>
                      {/* Hidden input to trigger file selection */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setSelectedFile(f);
                          if (f) {
                            handleUploadApplicationPdf(f);
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center"
                      >
                        <SafeIcon icon={FiVideo} className="mr-2" />
                        Upload Application PDF
                      </Button>
                      {uploadedPdfUrl && (
                        <div className="mt-3 w-full">
                          <div className="w-full border border-gray-200 bg-gray-50 rounded-lg p-3 sm:p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div className="flex items-start sm:items-center gap-2">
                                <SafeIcon icon={FiFileText} className="w-5 h-5 text-gray-600 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {getFileNameFromUrl(uploadedPdfUrl)}
                                  </p>
                                  <p className="text-xs text-green-700 mt-0.5">PDF uploaded</p>
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open(uploadedPdfUrl, '_blank')}
                                >
                                  View PDF
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async () => { try { await navigator.clipboard.writeText(uploadedPdfUrl); toast.success('Link copied'); } catch (_) { toast.error('Failed to copy'); } }}
                                >
                                  Copy Link
                                </Button>
                                <Button size="sm" onClick={handleCompleteProcess} loading={processing}>
                                  Complete Process
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 text-center break-all">
                        Meeting URL: <a href={getMeetingUrl(orderId)} target="_blank" rel="noreferrer" className="text-blue-600 underline">{getMeetingUrl(orderId)}</a>
                        <button
                          onClick={async () => { try { await navigator.clipboard.writeText(getMeetingUrl(orderId)); toast.success('Meeting link copied'); } catch (_) { /* ignore */ } }}
                          className="ml-2 text-blue-600 underline"
                        >
                          Copy
                        </button>
                      </p>
                    </div>
                  </div>

                  
                </div>
              )}
              
              {order.status === 'pdf_submitted' && (
                <div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">Application Submitted</h3>
                        <div className="mt-2 text-sm text-green-700">
                          <p>This application has been successfully submitted and is awaiting admin verification.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center">
                        <SafeIcon icon={FiFileText} className="w-5 h-5 text-gray-600 mr-2" />
                        <a
                          href={order.submitted_pdf_url || '#'}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 underline"
                        >
                          View uploaded PDF
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(order.submitted_pdf_url || '#', '_blank')}
                        >
                          View PDF
                        </Button>
                        <Button size="sm" onClick={handleCompleteProcess} loading={processing}>
                          Complete Process
                        </Button>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Submitted at:</span> {formatDate(order.completed_at)}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Status:</span> Waiting for admin verification
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
            
            {/* Upload Application PDF */}
            {order.status === 'in_progress' && showScreenShare && (
              <Card className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Upload Application PDF</h2>
                <p className="text-sm text-gray-600 mb-4">After completing the application with the customer, upload the success form PDF downloaded from the respective website.</p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                  <Button onClick={handleUploadApplicationPdf} loading={uploading} className="flex items-center">
                    <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2" />
                    Upload PDF
                  </Button>
                </div>
              </Card>
            )}
            
            {/* Documents */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Customer Documents</h2>
              <div className="space-y-4">
                {documents.length > 0 ? (
                  documents.map((doc, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiFileText} className="w-5 h-5 text-primary-600 mr-3" />
                        <span className="text-gray-900">{doc.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadFile(doc.url, `${doc.name}-${order.order_id}`)}
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600 text-center py-4">No documents available</p>
                )}
              </div>
            </Card>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Customer Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
              {customer && (
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="bg-gray-100 rounded-full w-12 h-12 flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="w-6 h-6 text-gray-600" />
                    </div>
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">{customer.name}</p>
                      <p className="text-sm text-gray-600">ID: {customer.id}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 pt-2">
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-medium text-gray-900">{customer.phone}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium text-gray-900">{customer.email}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <Button
                      variant="outline"
                      className="w-full flex items-center justify-center"
                      onClick={() => window.open(`tel:${customer.phone}`)}
                    >
                      <SafeIcon icon={FiPhone} className="mr-2" />
                      Call Customer
                    </Button>
                  </div>
                </div>
              )}
            </Card>
            
            {/* Order Summary */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Application Fee:</span>
                  <span>₹{order.application_fee}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service Fee:</span>
                  <span>₹{order.service_fee}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Total Amount:</span>
                  <span>₹{parseFloat(order.application_fee) + parseFloat(order.service_fee)}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Order Assigned:</span>
                    <span>{formatDate(order.assigned_at)}</span>
                  </div>
                  
                  {order.processing_started_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Processing Started:</span>
                      <span>{formatDate(order.processing_started_at)}</span>
                    </div>
                  )}
                  
                  {order.completed_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Completed:</span>
                      <span>{formatDate(order.completed_at)}</span>
                    </div>
                  )}
                  
                  {order.deadline && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Deadline:</span>
                      <span className="font-medium">{formatDate(order.deadline)}</span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
            
            {/* Guidelines */}
            <Card className="p-6 bg-blue-50">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Operator Guidelines</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Call the customer before starting the application</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Share your screen during the entire process</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Double-check all information before submission</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Pay application fee from your account</span>
                </li>
                <li className="flex items-start space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Submit completed PDF to admin for verification</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;